import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { countryPhoneData } from 'phone';
import { filter, firstValueFrom, Observable, Subscription } from 'rxjs';
import {
  Dietitian,
  DietitianProps,
  FEATURED_DIETITIAN_COUNTRY_CODE,
} from 'src/app/data/dietitian/domain/dietitian';

import {
  AccountInformationsFacade,
  AccountInformationsViewModel,
} from './account-informations.facade';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'msd-account-informations',
  templateUrl: './account-informations.component.html',
  styleUrls: ['./account-informations.component.scss'],
  providers: [AccountInformationsFacade],
})
export class AccountInformationsComponent implements OnInit {
  form: FormGroup;
  accountStatusSubscription?: Subscription;
  accountStatus$: Observable<AccountInformationsViewModel>;
  isLoading = true;

  featuredCountries = FEATURED_DIETITIAN_COUNTRY_CODE.map((code) =>
    countryPhoneData.find((country) => country.country_code === code),
  );
  countries = countryPhoneData;

  constructor(private facade: AccountInformationsFacade) {
    this.accountStatus$ = this.facade.accountStatus$;
    this.accountStatus$.subscribe();
  }

  ngOnInit(): void {
    this.form = new FormGroup({
      lastName: new FormControl('', [
        Validators.required,
        Validators.minLength(1),
      ]),
      firstName: new FormControl('', [
        Validators.required,
        Validators.minLength(1),
      ]),
      phone: new FormControl('', [
        Validators.required,
        Validators.minLength(1),
      ]),
      email: new FormControl('', [Validators.required, Validators.email]),
      address: new FormControl('', [Validators.required]),
      postalCode: new FormControl('', [Validators.required]),
      city: new FormControl('', [Validators.required]),
      country: new FormControl('', [Validators.required]),
      gender: new FormControl('', [Validators.required]),
      status: new FormControl('', [Validators.required]),
      registrationNumber: new FormControl(''),
    });

    firstValueFrom(
      this.accountStatus$.pipe(filter((state) => state.dietitian != null)),
    ).then((state) => {
      const dietitian = state.dietitian;
      this.form.patchValue({
        lastName: dietitian?.lastName,
        firstName: dietitian?.firstName,
        phone: dietitian?.phoneNumber,
        email: dietitian?.email,
        address: dietitian?.address,
        postalCode: dietitian?.postalCode,
        city: dietitian?.city,
        country: dietitian?.country?.toUpperCase(),
        gender: dietitian?.gender,
        status: dietitian?.status,
        registrationNumber: dietitian?.adeliNumber,
      });
    });

    this.accountStatusSubscription = this.accountStatus$.subscribe((state) => {
      this.isLoading = state.isLoading;
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async pictureSelected(event: any): Promise<Dietitian | undefined> {
    if (!event.target.files?.length) {
      return undefined;
    }
    const file = event?.target?.files[0];
    event.target.value = '';
    return this.updatePicture(file);
  }

  async pictureDropped(files: FileList): Promise<Dietitian> {
    return this.updatePicture(files[0]);
  }

  async updatePicture(file: File): Promise<Dietitian> {
    return this.facade.updatePicture(file);
  }

  deletePicture() {
    this.facade.deletePicture();
  }

  get lastNameInvalid(): boolean | undefined {
    return (
      !this.form.get('lastName')?.valid &&
      (this.form.get('lastName')?.touched || this.form.get('lastName')?.dirty)
    );
  }

  get genderInvalid(): boolean | undefined {
    return (
      !this.form.get('gender')?.valid &&
      (this.form.get('gender')?.touched || this.form.get('gender')?.dirty)
    );
  }

  get firstNameInvalid(): boolean | undefined {
    return (
      !this.form.get('firstName')?.valid &&
      (this.form.get('firstName')?.touched || this.form.get('firstName')?.dirty)
    );
  }

  get phoneInvalid(): boolean | undefined {
    return (
      !this.form.get('phone')?.valid &&
      (this.form.get('phone')?.touched || this.form.get('phone')?.dirty)
    );
  }

  get emailInvalid(): boolean | undefined {
    return (
      !this.form.get('email')?.valid &&
      (this.form.get('email')?.touched || this.form.get('email')?.dirty)
    );
  }

  get addressInvalid(): boolean | undefined {
    return (
      !this.form.get('address')?.valid &&
      (this.form.get('address')?.touched || this.form.get('address')?.dirty)
    );
  }

  get postalCodeInvalid(): boolean | undefined {
    return (
      !this.form.get('postalCode')?.valid &&
      (this.form.get('postalCode')?.touched ||
        this.form.get('postalCode')?.dirty)
    );
  }

  get cityInvalid(): boolean | undefined {
    return (
      !this.form.get('city')?.valid &&
      (this.form.get('city')?.touched || this.form.get('city')?.dirty)
    );
  }

  get countryInvalid(): boolean | undefined {
    return (
      !this.form.get('country')?.valid &&
      (this.form.get('country')?.touched || this.form.get('country')?.dirty)
    );
  }

  submit() {
    if (this.form.valid) {
      this.facade.updateAccount(<DietitianProps>{
        firstName: this.form.get('firstName')?.value,
        lastName: this.form.get('lastName')?.value,
        phoneNumber: this.form.get('phone')?.value,
        email: this.form.get('email')?.value,
        address: this.form.get('address')?.value,
        postalCode: this.form.get('postalCode')?.value,
        city: this.form.get('city')?.value,
        country: this.form.get('country')?.value?.toLowerCase(),
        gender: this.form.get('gender')?.value,
        status: this.form.get('status')?.value,
        adeliNumber: this.form.get('registrationNumber')?.value,
      });
    }
  }

  ngOnDestroy(): void {
    this.accountStatusSubscription?.unsubscribe();
  }

  hasRegistrationNumber() {
    const statusCtrl = this.form.get('status');
    if (statusCtrl) {
      return statusCtrl.value === 'dietitian';
    }
    return false;
  }
}
